import { useT } from "@/i18n";
import {
  formatMaintenanceTime,
  localizeMaintenanceText,
  MaintenanceSchedule,
} from "@/lib/maintenance/schedule";
import { Alert, cn, IconArrowLink, IconButton, IconClose, TextButtonLink } from "@/ui";

type MaintenanceBannerProps = {
  schedule: MaintenanceSchedule;
  phase: "upcoming" | "inProgress";
  locale: string;
  onDismiss?: () => void;
};

export const MaintenanceBanner = ({
  schedule,
  phase,
  locale,
  onDismiss,
}: MaintenanceBannerProps) => {
  const t = useT();

  const isInProgress = phase === "inProgress";
  const start = formatMaintenanceTime(schedule.startsAt, locale);
  const end = schedule.endsAt ? formatMaintenanceTime(schedule.endsAt, locale) : null;

  const fallbackTitle = isInProgress
    ? t("maintenance.components.notice.inProgressTitle")
    : t("maintenance.components.notice.upcomingTitle");
  const fallbackInProgressMessage = end
    ? t("maintenance.components.notice.inProgressMessage", { end })
    : t("maintenance.components.notice.inProgressMessageNoEnd");
  const fallbackUpcomingMessage = end
    ? t("maintenance.components.notice.upcomingMessage", { start, end })
    : t("maintenance.components.notice.upcomingMessageNoEnd", { start });

  const title = localizeMaintenanceText(schedule, phase, "title", locale) ?? fallbackTitle;
  const message =
    localizeMaintenanceText(schedule, phase, "message", locale) ??
    (isInProgress ? fallbackInProgressMessage : fallbackUpcomingMessage);

  return (
    <div className="fixed inset-x-4 bottom-4 z-[2147483000] mx-auto max-w-[640px] rounded-md bg-surface-1">
      <Alert
        variant={schedule.severity}
        title={title}
        description={
          <div className="flex flex-col items-start gap-1">
            <span>{message}</span>
            {schedule.link && (
              <TextButtonLink
                href={schedule.link}
                target="_blank"
                rel="noopener noreferrer"
                color="brand"
                endIcon={IconArrowLink}
              >
                {t("maintenance.components.notice.moreInfo")}
              </TextButtonLink>
            )}
          </div>
        }
        className={cn(onDismiss && "pr-12")}
      />
      {onDismiss && (
        <IconButton
          icon={IconClose}
          label={t("maintenance.components.notice.dismiss")}
          onPress={onDismiss}
          size="sm"
          variant="tertiary"
          color="muted"
          className="absolute right-2 top-2"
        />
      )}
    </div>
  );
};
